/**
 * Reactive room state: the single place where a server snapshot becomes UI
 * state, and the single place user intent turns into a command.
 *
 * There is deliberately no client-side room state machine. A snapshot is
 * applied verbatim; an accepted command's effects are *not* applied locally
 * (the server broadcasts a fresh snapshot after every accepted mutation, and
 * applying both would double-count). A rejection is surfaced as a notice and
 * the already-arriving snapshot is the recovery.
 *
 * The one command this module sends without a click is the join rename, and it
 * is fired at most once per connection, only when the remembered name differs
 * from the name the server assigned. That is pre-existing user intent (the name
 * they typed), not a reaction to snapshot content — the flag makes a
 * snapshot/command loop structurally impossible.
 */

import type {
  ClockEstimate,
} from '../player/clock';
import { PlaybackController } from '../player/controller.svelte';
import type {
  Participant,
  PlaybackState,
  QueueEntry,
  Revision,
  RoomSettings,
  RoomSnapshot,
  YouInfo,
} from '../generated';
import { SocialState } from '../social/social.svelte';
import { RoomClient, type CommandOutcome, type ConnectionStatus } from './client';
import { commandAllowed, deniedReason, permissionsFor, type RoomPermissions } from './permissions';
import type { CommandIntent, CommandKind } from './protocol';

export interface Notice {
  /** Changes on every new notice so the live region re-announces it. */
  readonly id: number;
  readonly severity: 'error' | 'warning' | 'info';
  readonly text: string;
}

const IDLE_PLAYBACK: PlaybackState = { state: 'idle' };

/**
 * How a rejection is surfaced, or `null` when it deliberately produces no
 * notice at all.
 */
function rejectionNotice(
  kind: CommandKind,
  reason: string,
  message: string,
): { readonly severity: Notice['severity']; readonly text: string } | null {
  // A sponsor skip that lost a race — the room already skipped this segment,
  // or the playhead moved past it before the ack arrived — is the expected
  // outcome of a second click, not a failure worth alarming anyone about. The
  // indicator has already disappeared, which is the feedback.
  if (
    kind === 'skip-sponsor-segment' &&
    (reason === 'invalid-state' || reason === 'not-found' || reason === 'stale-revision')
  ) {
    return null;
  }
  switch (reason) {
    case 'stale-revision':
      return {
        severity: 'error',
        text: 'The queue changed while you were looking at it. Your view is now up to date — try that again.',
      };
    case 'stale-epoch':
      return {
        severity: 'error',
        text: 'The room was reset while you were away. The current room state is shown now.',
      };
    case 'permission-denied':
      return { severity: 'error', text: message };
    case 'not-found':
      return { severity: 'error', text: 'That queue entry is already gone.' };
    // The server's social rate limit. Expected under a flood, and the local
    // debounce already absorbs ordinary double clicks — a warning, not an error.
    case 'rate-limited':
      return { severity: 'warning', text: message === '' ? 'Slow down a little.' : message };
    default:
      return {
        severity: 'error',
        text: message === '' ? `The server rejected that action (${kind}).` : message,
      };
  }
}

export class RoomState {
  snapshot = $state<RoomSnapshot | null>(null);
  connection = $state<ConnectionStatus>({
    phase: 'connecting',
    attempt: 0,
    nextRetryInMs: null,
    message: null,
  });
  clock = $state<ClockEstimate | null>(null);
  notice = $state<Notice | null>(null);
  /** Connection-level server error (`ServerMessage::Error`). */
  serverError = $state<string | null>(null);
  /** In-flight command count per command kind, for busy affordances. */
  busy = $state<Partial<Record<CommandKind, number>>>({});

  readonly player: PlaybackController;
  /** Chat log and active reactions (`src/social/`). */
  readonly social: SocialState;

  readonly #client: RoomClient;
  #desiredUsername: string;
  #renameSent = false;
  #noticeSeq = 0;
  #lastEpoch: bigint | null = null;

  constructor(desiredUsername: string) {
    this.#desiredUsername = desiredUsername;
    this.#client = new RoomClient({
      onSnapshot: (snapshot) => this.#applySnapshot(snapshot),
      onStatus: (status) => this.#applyStatus(status),
      onClock: (estimate) => {
        this.clock = estimate;
      },
      onChat: (message) => this.social.receiveChat(message),
      onReaction: (reaction) => this.social.receiveReaction(reaction),
      onServerError: (reason, message) => {
        this.serverError = message;
        this.#notify('error', `${message} (${reason})`);
      },
    });
    this.player = new PlaybackController({
      serverNowMs: () => this.#client.serverNowMs(),
      report: (message) => this.#client.sendPlaybackReport(message),
    });
    this.social = new SocialState((intent) => this.run(intent));
  }

  start(): void {
    this.player.start();
    this.#client.connect();
  }

  stop(): void {
    this.player.dispose();
    this.social.dispose();
    this.#client.dispose();
  }

  get you(): YouInfo | null {
    return this.snapshot?.you ?? null;
  }

  get settings(): RoomSettings | null {
    return this.snapshot?.settings ?? null;
  }

  get participants(): readonly Participant[] {
    return this.snapshot?.participants ?? [];
  }

  get queue(): readonly QueueEntry[] {
    return this.snapshot?.queue ?? [];
  }

  get playback(): PlaybackState {
    return this.snapshot?.playback ?? IDLE_PLAYBACK;
  }

  get revision(): Revision | null {
    return this.snapshot?.revision ?? null;
  }

  get permissions(): RoomPermissions | null {
    const snapshot = this.snapshot;
    return snapshot === null ? null : permissionsFor(snapshot.you, snapshot.settings);
  }

  /** True while the socket is healthy enough to accept commands. */
  get live(): boolean {
    return this.connection.phase === 'open';
  }

  get desiredUsername(): string {
    return this.#desiredUsername;
  }

  isBusy(kind: CommandKind): boolean {
    return (this.busy[kind] ?? 0) > 0;
  }

  /** Estimated server clock, for the sync readout and instrumentation. */
  serverNowMs(): number | null {
    return this.#client.serverNowMs();
  }

  dismissNotice(): void {
    this.notice = null;
  }

  /**
   * Sends one command. Resolves with the outcome; rejections and local
   * failures are also surfaced as a notice so no action fails silently.
   */
  async run(intent: CommandIntent): Promise<CommandOutcome> {
    const permissions = this.permissions;
    if (permissions !== null && !commandAllowed(intent.type, permissions)) {
      const reason = deniedReason(intent.type, permissions) ?? 'You cannot do that in this room.';
      this.#notify('warning', reason);
      return { result: 'failed', reason: 'permission-denied-locally', message: reason };
    }

    this.busy[intent.type] = (this.busy[intent.type] ?? 0) + 1;
    try {
      const outcome = await this.#client.send(intent);
      if (outcome.result === 'rejected') {
        const notice = rejectionNotice(intent.type, outcome.reason, outcome.message);
        if (notice !== null) this.#notify(notice.severity, notice.text);
      } else if (outcome.result === 'failed') {
        this.#notify('warning', outcome.message);
      }
      return outcome;
    } finally {
      const remaining = (this.busy[intent.type] ?? 1) - 1;
      if (remaining <= 0) delete this.busy[intent.type];
      else this.busy[intent.type] = remaining;
    }
  }

  /**
   * Changes the remembered display name and renames in the room. Storing the
   * preference is the caller's concern (`session/username.ts`).
   */
  async setUsername(username: string): Promise<CommandOutcome> {
    this.#desiredUsername = username;
    this.#renameSent = true;
    return this.run({ type: 'rename', username });
  }

  #applySnapshot(snapshot: RoomSnapshot): void {
    if (this.#lastEpoch !== null && this.#lastEpoch !== snapshot.epoch) {
      // A new epoch means the previous room — queue, roles, media, chat — is
      // gone. The chat log is cleared rather than merged: those messages
      // belong to a room that no longer exists.
      this.#notify('info', 'The room was reset. This is the fresh room state.');
      this.player.requestResync();
      this.social.reset();
    }
    this.#lastEpoch = snapshot.epoch;
    this.snapshot = snapshot;
    this.serverError = null;
    this.player.applyPlayback(snapshot.playback);
    this.social.applySnapshot(snapshot);

    if (!this.#renameSent && this.#desiredUsername !== '' && snapshot.you.username !== this.#desiredUsername) {
      this.#renameSent = true;
      void this.run({ type: 'rename', username: this.#desiredUsername });
    }
  }

  #applyStatus(status: ConnectionStatus): void {
    const wasLive = this.connection.phase === 'open';
    this.connection = status;
    if (status.phase === 'open') {
      // The room may have moved arbitrarily while the socket was down; the
      // fresh snapshot plus a hard realign is the only honest recovery.
      this.player.setConnected(true);
      return;
    }
    this.player.setConnected(false);
    // A dropped connection invalidates the once-per-connection join rename.
    this.#renameSent = false;
    if (wasLive && status.phase === 'reconnecting') {
      this.#notify('warning', 'Connection lost. Local playback is paused until the room is back.');
    }
  }

  #notify(severity: Notice['severity'], text: string): void {
    this.#noticeSeq += 1;
    this.notice = { id: this.#noticeSeq, severity, text };
  }
}
